import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModuleRewards } from "@/hooks/useModuleRewards";
import { useCurriculumProgress } from "@/hooks/useCurriculumProgress";
import ModuleShell from "@/components/modules/ModuleShell";
import ModuleStepCard from "@/components/modules/ModuleStepCard";
import ModuleQuiz from "@/components/modules/ModuleQuiz";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ArrowRight, Receipt, Percent, Building, CheckCircle2, TrendingDown, PiggyBank, Calculator, Scale } from "lucide-react";

type Step = "intro" | "income_types" | "brackets" | "account_structure" | "deductions" | "planning" | "quiz" | "summary";
const STEPS: Step[] = ["intro", "income_types", "brackets", "account_structure", "deductions", "planning", "quiz", "summary"];

const TaxStrategyModule = () => {
  const navigate = useNavigate();
  const { moduleGates, loading } = useCurriculumProgress();
  const { completeModule } = useModuleRewards();
  const [step, setStep] = useState<Step>("intro");
  const [income, setIncome] = useState(75000);
  const [accountChoice, setAccountChoice] = useState<string | null>(null);
  const [deductionChoice, setDeductionChoice] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);

  const gate = moduleGates.find(g => g.moduleKey === "tax_strategy");
  const isLocked = !gate?.isUnlocked;
  if (loading) return null;

  // Simplified 2024 brackets
  const calcTax = (inc: number) => {
    if (inc <= 11600) return inc * 0.10;
    if (inc <= 47150) return 1160 + (inc - 11600) * 0.12;
    if (inc <= 100525) return 5426 + (inc - 47150) * 0.22;
    return 17168.50 + (inc - 100525) * 0.24;
  };
  const totalTax = Math.round(calcTax(income));
  const effectiveRate = ((totalTax / income) * 100).toFixed(1);

  return (
    <ModuleShell
      title="Tax Strategy"
      stepIndex={STEPS.indexOf(step)}
      totalSteps={STEPS.length}
      isLocked={isLocked}
      lockMessage={gate?.missingPrereqs?.length ? `Complete: ${gate.missingPrereqs.join(", ")}` : "Complete Taxes to unlock."}
      isCompleted={completed}
      completedMessage="You understand tax brackets, account structures, and tax-aware planning."
      stepKey={step}
    >
      {step === "intro" && (
        <ModuleStepCard
          icon={<Receipt className="w-8 h-8 text-primary" />}
          title="Tax Strategy"
          body="Taxes are your largest expense over a lifetime. Understanding how different income is taxed, and how account structure affects what you keep, is essential for wealth building."
          highlights={["It's not what you earn — it's what you keep", "Account structure is tax strategy", "Legal tax optimization is smart, not shady"]}
          action={<Button onClick={() => setStep("income_types")} className="w-full gap-2">Let's Begin <ArrowRight className="w-4 h-4" /></Button>}
        />
      )}

      {step === "income_types" && (
        <ModuleStepCard
          icon={<Percent className="w-8 h-8 text-accent" />}
          title="Not All Income Is Taxed Equal"
          body="Earned income (salary, wages) is taxed the highest. Capital gains (investment profits held 1+ year) get lower rates. Qualified dividends get preferential rates. Roth withdrawals are tax-free."
          highlights={[
            "W-2 wages: taxed at ordinary rates",
            "Long-term capital gains: 0%, 15%, or 20%",
            "Roth IRA withdrawals: $0 in taxes",
            "1099 income: pay self-employment tax too",
          ]}
          action={<Button onClick={() => setStep("brackets")} className="w-full gap-2">Next: Tax Brackets <ArrowRight className="w-4 h-4" /></Button>}
        />
      )}

      {step === "brackets" && (
        <div className="space-y-6">
          <ModuleStepCard
            icon={<Calculator className="w-8 h-8 text-primary" />}
            title="Marginal vs Effective Tax Rate"
            body="You're NOT taxed at one flat rate. Each dollar is taxed at the rate for its bracket. Your effective rate is always lower than your marginal rate."
            subtext={`Income: $${income.toLocaleString()} · Tax: $${totalTax.toLocaleString()} · Effective Rate: ${effectiveRate}%`}
            action={<div />}
          />
          <Slider value={[income]} min={30000} max={200000} step={5000} onValueChange={([v]) => setIncome(v)} />
          <div className="flex justify-between text-xs text-muted-foreground"><span>$30k</span><span>$200k</span></div>
          <div className="rounded-2xl border border-border bg-card/50 p-4 space-y-2">
            <div className="flex justify-between text-xs"><span className="text-muted-foreground">Marginal bracket</span><span className="text-foreground font-mono">{income <= 11600 ? "10%" : income <= 47150 ? "12%" : income <= 100525 ? "22%" : "24%"}</span></div>
            <div className="flex justify-between text-xs"><span className="text-muted-foreground">Effective rate</span><span className="text-primary font-mono">{effectiveRate}%</span></div>
            <div className="flex justify-between text-xs"><span className="text-muted-foreground">Take-home</span><span className="text-foreground font-mono">${(income - totalTax).toLocaleString()}</span></div>
          </div>
          <Button onClick={() => setStep("account_structure")} className="w-full gap-2">Next: Account Structure <ArrowRight className="w-4 h-4" /></Button>
        </div>
      )}

      {step === "account_structure" && (
        <div className="space-y-6">
          <ModuleStepCard icon={<Building className="w-8 h-8 text-accent" />} title="Account Structure = Tax Strategy" body="Where you put your money determines when (or if) it gets taxed:" action={<div />} />
          <div className="grid gap-3">
            {[
              { key: "traditional", label: "Traditional 401(k)/IRA", desc: "Tax-deferred: reduce taxable income now, pay taxes on withdrawals in retirement." },
              { key: "roth", label: "Roth IRA/401(k)", desc: "Tax-free growth: pay taxes now, withdrawals in retirement are completely tax-free." },
              { key: "taxable", label: "Taxable Brokerage", desc: "No tax benefits, but no restrictions. Capital gains taxed when you sell." },
            ].map(a => (
              <button key={a.key} className={`rounded-2xl border p-4 text-left transition-all ${accountChoice === a.key ? "border-primary bg-primary/10" : "border-border bg-card/50 hover:border-primary/30"}`} onClick={() => setAccountChoice(a.key)}>
                <p className="font-heading font-bold text-sm text-foreground">{a.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{a.desc}</p>
              </button>
            ))}
          </div>
          <Button onClick={() => setStep("deductions")} disabled={!accountChoice} className="w-full gap-2">Next: Deductions <ArrowRight className="w-4 h-4" /></Button>
        </div>
      )}

      {step === "deductions" && (
        <div className="space-y-6">
          <ModuleStepCard icon={<TrendingDown className="w-8 h-8 text-primary" />} title="Deductions & Credits" body="Deductions reduce your taxable income. Credits reduce your actual tax bill dollar-for-dollar. Credits are more valuable." action={<div />} />
          <div className="grid gap-3">
            {[
              { key: "standard", label: "Standard Deduction ($14,600)", desc: "Most people take this. Simple, no receipts needed." },
              { key: "itemized", label: "Itemize Deductions", desc: "Only if your deductions exceed the standard. Requires tracking mortgage interest, charity, medical, etc." },
            ].map(d => (
              <button key={d.key} className={`rounded-2xl border p-4 text-left transition-all ${deductionChoice === d.key ? "border-primary bg-primary/10" : "border-border bg-card/50 hover:border-primary/30"}`} onClick={() => setDeductionChoice(d.key)}>
                <p className="font-heading font-bold text-sm text-foreground">{d.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{d.desc}</p>
              </button>
            ))}
          </div>
          <Button onClick={() => setStep("planning")} disabled={!deductionChoice} className="w-full gap-2">Next: Tax Planning <ArrowRight className="w-4 h-4" /></Button>
        </div>
      )}

      {step === "planning" && (
        <ModuleStepCard
          icon={<Scale className="w-8 h-8 text-accent" />}
          title="Tax-Aware Planning"
          body="Smart tax planning means thinking about taxes before making financial decisions, not just at tax time. Maximize tax-advantaged accounts first, harvest losses to offset gains, and match income timing to life stages."
          highlights={[
            "Max out employer 401(k) match first (free money)",
            "Roth is powerful when you expect higher future taxes",
            "Tax-loss harvesting offsets capital gains",
            "Charitable giving reduces taxable income",
          ]}
          action={<Button onClick={() => setStep("quiz")} className="w-full gap-2">Take Knowledge Check <ArrowRight className="w-4 h-4" /></Button>}
        />
      )}

      {step === "quiz" && (
        <ModuleQuiz moduleKey="tax_strategy" onPass={() => { setCompleted(true); setStep("summary"); completeModule("tax_strategy"); }} />
      )}

      {step === "summary" && (
        <div className="space-y-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto border border-primary/20">
            <CheckCircle2 className="w-8 h-8 text-primary" />
          </div>
          <h2 className="font-heading text-xl font-bold text-foreground">Tax Strategy Complete</h2>
          <p className="text-sm text-muted-foreground">You understand brackets, account structures, and tax-aware planning. Keep what you earn.</p>
          <Button onClick={() => navigate("/dashboard")} className="w-full">Back to Dashboard</Button>
        </div>
      )}
    </ModuleShell>
  );
};

export default TaxStrategyModule;
